<template>
  <div class="MyMapSousDomaineIndicateurs" v-show="isShown">
    <div class="MyMapSousDomaineIndicateurs__header">
      <button
        type="button"
        class="MyMapSousDomaineIndicateurs__back"
        @click="emit('back')"
        :aria-label="$t('common.return', 'Retour')"
      >
        <Icon icon="mdi:arrow-left" color="dark-grey" :size="24" />
      </button>
      <h3 class="MyMapSousDomaineIndicateurs__title">{{ libelleDynamique(sousDomaine.sousDomaineLib, sousDomaine.sousDomaineLibEn) }}</h3>
    </div>
    <div class="MyMapSousDomaineIndicateurs__indicateursBlock">
      <span class="MyMapSousDomaineIndicateurs__indicateursLabel">
        {{ $t('myMap.sousDomaines.indicateurs', 'Indicateurs') }}
      </span>
      <div v-if="indicateurs.length > 0" class="MyMapSousDomaineIndicateurs__indicateurs">
        <MyMapIndicateur
          v-for="indicateur in indicateurs"
          :key="indicateur.atgId"
          :indicateur="indicateur"
          :domaine-code="domaineCode"
          :infrastructure-layer-name="infrastructureLayerName"
        />
      </div>
      <p v-else class="MyMapSousDomaineIndicateurs__noIndicateurs">
        {{ $t('myMap.sousDomaines.noIndicateurs', 'Aucun indicateur disponible') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SousDomaine } from '@/models/interfaces/SousDomaine'
import { libelleDynamique } from '@/utils/libelleDynamique'
import type { Attribut } from '@/models/interfaces/Attribut'
import { useMyMapStore } from '@/stores/myMapStore'
import { useAttributStore } from '@/stores/attributStore'
import Icon from '@/components/ui/Icon.vue'
import { filterIndicatorsForSelectedCommune } from '@/services/map/IndicatorCommuneVisibilityService'
import MyMapIndicateur from '@/views/map/components/Domaines/MyMapIndicateur.vue'
import { computed, ref, onMounted, watch } from 'vue'

const props = defineProps<{
  sousDomaine: SousDomaine
  domaineCode: string
  domaineLib?: string
  infrastructureLayerName?: string
}>()

const emit = defineEmits<{
  back: []
}>()

const mapStore = useMyMapStore()
const attributStore = useAttributStore()
const visibleIndicateurs = ref<Attribut[]>([])
let refreshGeneration = 0

const isShown = computed(() => !mapStore.activeAtlas.leftPanel.active)

const baseIndicateurs = computed(() =>
  attributStore.getIndicateursBySousdomaineId(props.sousDomaine.sousDomaineId),
)

const isCommuneIndicatorFilterActive = computed(
  () => mapStore.lockedCommuneName != null && mapStore.communeViewData?.geometry != null,
)

const indicateurs = computed(() => visibleIndicateurs.value)

async function refreshVisibleIndicateurs(): Promise<void> {
  const generation = ++refreshGeneration
  const base = baseIndicateurs.value

  if (!isCommuneIndicatorFilterActive.value) {
    visibleIndicateurs.value = base
    return
  }

  const filtered = await filterIndicatorsForSelectedCommune(base, {
    map: mapStore.myMap?.map,
    communeGeometry: mapStore.communeViewData?.geometry,
    communeKey: mapStore.lockedCommuneName ?? '',
  })

  if (generation !== refreshGeneration) return
  visibleIndicateurs.value = filtered
}

onMounted(async () => {
  if (attributStore.indicateurPcdList.length === 0) {
    await attributStore.getIndicateursPcd()
  }
  await refreshVisibleIndicateurs()
})

watch(
  [
    baseIndicateurs,
    () => mapStore.lockedCommuneName,
    () => mapStore.communeViewData?.name,
    () => mapStore.myMap?.map,
  ],
  () => {
    void refreshVisibleIndicateurs()
  },
  { immediate: true },
)
</script>

<style lang="scss">
.MyMapSousDomaineIndicateurs {
  display: flex;
  flex-flow: column nowrap;
  gap: var(--panel-nested-gap);
  padding-left: 0;
}

.MyMapSousDomaineIndicateurs__indicateursBlock {
  display: flex;
  flex-flow: column nowrap;
  gap: 0;
  border: 1px solid var(--neutral-border);
  border-radius: var(--dim-radius);
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}

.MyMapSousDomaineIndicateurs__indicateursLabel {
  font-size: var(--panel-section-title-size);
  font-weight: var(--panel-section-title-weight);
  letter-spacing: var(--panel-section-title-tracking);
  text-transform: uppercase;
  color: rgb(var(--v-theme-main-blue));
  padding: 0.375rem 0.5rem;
  background: var(--neutral-surface-subtle);
  border-bottom: 1px solid var(--neutral-border);
}

.MyMapSousDomaineIndicateurs__header {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--neutral-border);
}

.MyMapSousDomaineIndicateurs__title {
  font-size: var(--panel-row-title-size);
  font-weight: var(--panel-row-title-weight);
  color: rgb(var(--v-theme-main-blue));
  margin: 0;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  text-transform: uppercase;
}

.MyMapSousDomaineIndicateurs__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-width: 2.5rem;
  width: 2.5rem;
  height: 2.5rem;
  background: transparent;
  border: 1px solid var(--neutral-border);
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-theme-main-blue), 0.08);
    border-color: rgb(var(--v-theme-main-blue));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-main-blue));
    outline-offset: 2px;
  }
}

.MyMapSousDomaineIndicateurs__indicateurs {
  display: flex;
  flex-flow: column nowrap;
  gap: 0;
}

.MyMapSousDomaineIndicateurs__noIndicateurs {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
  margin: 0;
  padding: 0.5rem 0.5rem;
}
</style>
